import {
  ADD_BOOK,
  EDIT_BOOK,
  DELETE_BOOK,
  REORDER_BOOKS,
  LOAD_BOOKS,
  ADD_BOOK_FROM_TEMPLATE,
  EDIT_BOOK_IMAGE,
  SET_BOOK_TITLE,
  SET_BOOK_PREMISE,
  SET_BOOK_GENRE,
  SET_BOOK_THEME,
  DUPLICATE_BOOK,
} from '../constants/ActionTypes'
import { book } from '../store/initialState'

/**
 * @typedef {import('../index').Book} Book
 * @typedef {import('../index').DOMSelection} DOMSelection
 * @typedef {import('../index').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 * @typedef {import('../types/plottr').HierarchyLevelsPlottrFile} HierarchyLevelsPlottrFile
 * @typedef {import('../types/system').PlotlineCustomTemplateData} PlotlineCustomTemplateData
 * @typedef {import('../types/system').PlotlineTemplateData} PlotlineTemplateData
 * @typedef {import('./project').AddBookFromPLTRAction} AddBookFromPLTRAction
 */

/**
 * @typedef {{ type: 'DELETE_BOOK', id: number, linesToDelete: Array<number>, bookId: 'series' | number }} DeleteBookAction
 * @typedef {{ type: 'ADD_BOOK_FROM_TEMPLATE', book: Book, templateData: PlotlineTemplateDataForAddBookAction, id: String, nextCardId: number, nextLineId: number, nextBeatId: number, title: String, premise: String, genre: String, theme: String, newBookId: number }} AddBookFromTemplateAction
 * @typedef {Omit<AddBookFromPLTRAction, 'type'> & { type: 'DUPLICATE_BOOK', newBeats: PlottrFileBeatsEntry, newBookId: number }} DuplicateBookAction
 * @typedef {{ type: 'ADD_BOOK', book: Book, title: String, premise: String, genre: String, theme: String, newBookId: number }} AddBookAction
 */

/**
 * @typedef {ReturnType<typeof editBook> | ReturnType<typeof editBookContent> | ReturnType<typeof setBookTitle> | ReturnType<typeof setBookPremise> | ReturnType<typeof setBookGenre> | ReturnType<typeof setBookTheme> | ReturnType<typeof editBookImage> | ReturnType<typeof reorderBooks> | ReturnType<typeof load> | AddBookFromTemplateAction | DuplicateBookAction | DeleteBookAction | AddBookAction} BooksActions
 */

/**
 * @return {{ type: 'ADD_BOOK', book: Book, title: String, premise: String, genre: String, theme: String }}
 */
export function addBook(title = '', premise = '', genre = '', theme = '') {
  return { type: ADD_BOOK, book, title, premise, genre, theme }
}

/**
 * @typedef {PlotlineCustomTemplateData | PlotlineTemplateData} PlotlineTemplateDataForAddBookAction
 */

/**
 * @param {PlotlineTemplateDataForAddBookAction} templateData
 * @param {String} id
 * @return {{ type: 'ADD_BOOK_FROM_TEMPLATE', book: Book, templateData: PlotlineTemplateDataForAddBookAction, id: String }}
 */
export function addBookFromTemplate(templateData, id) {
  return { type: ADD_BOOK_FROM_TEMPLATE, book, templateData, id }
}

/**
 * @param {number} id
 * @param {String} title
 * @param {String} premise
 * @param {String} genre
 * @param {String} theme
 * @return {{ type: 'EDIT_BOOK', id: number, title: String, premise: String, genre: String, theme: String }}
 */
export function editBook(id, title, premise, genre, theme) {
  return { type: EDIT_BOOK, id, title, premise, genre, theme }
}

/**
 * @return {{ type: 'EDIT_BOOK' } & Book}
 */
export function editBookContent(bookContent = book) {
  return { type: EDIT_BOOK, ...bookContent }
}

/**
 * @param {number} id
 * @param {String} title
 * @param {DOMSelection} selection
 * @return {{ type: 'SET_BOOK_TITLE', id: number, title: String, selection: DOMSelection }}
 */
export function setBookTitle(id, title, selection) {
  return { type: SET_BOOK_TITLE, id, title, selection }
}

/**
 * @param {number} id
 * @param {String} premise
 * @param {DOMSelection} selection
 * @return {{ type: 'SET_BOOK_PREMISE', id: number, premise: String, selection: DOMSelection }}
 */
export function setBookPremise(id, premise, selection) {
  return { type: SET_BOOK_PREMISE, id, premise, selection }
}

/**
 * @param {number} id
 * @param {String} genre
 * @param {DOMSelection} selection
 * @return {{ type: 'SET_BOOK_GENRE', id: number, genre: String, selection: DOMSelection }}
 */
export function setBookGenre(id, genre, selection) {
  return { type: SET_BOOK_GENRE, id, genre, selection }
}

/**
 * @param {number} id
 * @param {String} theme
 * @param {DOMSelection} selection
 * @return {{ type: 'SET_BOOK_THEME', id: number, theme: String, selection: DOMSelection }}
 */
export function setBookTheme(id, theme, selection) {
  return { type: SET_BOOK_THEME, id, theme, selection }
}

/**
 * @param {number} id
 * @param {number} imageId
 * @return {{ type: 'EDIT_BOOK_IMAGE', id: number, imageId: number }}
 */
export function editBookImage(id, imageId) {
  return { type: EDIT_BOOK_IMAGE, id, imageId }
}

/**
 * @param {number} id
 * @return {{ type: 'DELETE_BOOK', id: number }}
 */
export function deleteBook(id) {
  return { type: DELETE_BOOK, id }
}

/**
 * @param {Array<number>} ids
 * @return {{ type: 'REORDER_BOOKS', ids: Array<number> }}
 */
export function reorderBooks(ids) {
  return { type: REORDER_BOOKS, ids }
}

/**
 * @param {boolean} patching
 * @param {Array<Book>} books
 * @return {{ type: 'LOAD_BOOKS', patching: boolean, books: Array<Book> }}
 */
export function load(patching, books) {
  return { type: LOAD_BOOKS, patching, books }
}

/**
 * @param {number} id
 * @return {{ type: 'DUPLICATE_BOOK', id: number }}
 */
export function duplicateBook(id) {
  return { type: DUPLICATE_BOOK, id }
}
